import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import PageShell from '../components/ui/PageShell'
import PageHeader from '../components/ui/PageHeader'
import SectionCard from '../components/ui/SectionCard'
import FormField from '../components/ui/FormField'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import DataTable from '../components/ui/DataTable'
import LoadingState from '../components/ui/LoadingState'
import ErrorState from '../components/ui/ErrorState'
import EmptyState from '../components/ui/EmptyState'
import useNotificationStore from '../services/notificationStore'
import {
  getMobileAppRelease,
  getMobileAppReleaseEvents,
  previewMobileAppReleaseSelection,
  publishMobileAppRelease,
  revokeMobileAppRelease,
  updateMobileAppRelease,
  uploadMobileAppReleaseApk,
} from '../services/mobileAppReleaseService'
import { formatDateTime } from '../utils/date'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import { formatVersionWithCode } from '../utils/versionLabel'

function redactSignedUrl(url) {
  const text = String(url || '')
  if (!text) return '-'
  try {
    const parsed = new URL(text)
    parsed.search = ''
    return parsed.toString()
  } catch {
    return text
  }
}

function maskedHash(value) {
  const hash = String(value || '')
  if (!hash) return '-'
  return `${hash.slice(0, 12)}...${hash.slice(-12)}`
}

function validateDraftForm(form) {
  const errors = {}
  if (form.platform !== 'android') errors.platform = 'Only android platform is supported'
  if (!form.channel || form.channel === 'production') errors.channel = 'Production channel is not allowed'
  if (!String(form.packageName || '').trim()) errors.packageName = 'Package name is required'
  if (!String(form.versionName || '').trim()) errors.versionName = 'Version name is required'

  const versionCode = Number(form.versionCode)
  if (!Number.isInteger(versionCode) || versionCode <= 0) {
    errors.versionCode = 'Version code must be a positive integer'
  }

  const minSupportedVersionCode = form.minSupportedVersionCode === ''
    ? null
    : Number(form.minSupportedVersionCode)
  if (minSupportedVersionCode != null && (!Number.isInteger(minSupportedVersionCode) || minSupportedVersionCode <= 0)) {
    errors.minSupportedVersionCode = 'Min supported version must be a positive integer'
  }
  if (minSupportedVersionCode != null && Number.isInteger(versionCode) && minSupportedVersionCode > versionCode) {
    errors.minSupportedVersionCode = 'Min supported version must not exceed version code'
  }

  const rolloutPercent = Number(form.rolloutPercent)
  if (!Number.isInteger(rolloutPercent) || rolloutPercent < 0 || rolloutPercent > 100) {
    errors.rolloutPercent = 'Rollout must be 0-100'
  }

  if (!String(form.apkUrl || '').trim()) errors.apkUrl = 'APK URL is required'
  if (!/^[a-f0-9]{64}$/i.test(String(form.sha256 || '').trim())) {
    errors.sha256 = 'SHA-256 must be 64 hex characters'
  }

  return errors
}

function toDraftForm(release) {
  return {
    platform: release?.platform || 'android',
    channel: release?.channel || 'debug',
    packageName: release?.packageName || '',
    versionName: release?.versionName || '',
    versionCode: release?.versionCode == null ? '' : String(release.versionCode),
    minSupportedVersionCode: release?.minSupportedVersionCode == null ? '' : String(release.minSupportedVersionCode),
    apkUrl: release?.apkUrl || '',
    sha256: release?.sha256 || '',
    sizeBytes: release?.sizeBytes == null ? '' : String(release.sizeBytes),
    shortDescription: release?.shortDescription || '',
    releaseNotes: release?.longDescription || release?.releaseNotes || '',
    mandatory: Boolean(release?.mandatory),
    rolloutPercent: release?.rolloutPercent == null ? 100 : release.rolloutPercent,
    expiresAt: release?.expiresAt ? String(release.expiresAt).slice(0, 16) : '',
  }
}

export default function MobileAppReleaseDetail() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const showToast = useNotificationStore((state) => state.showToast)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [release, setRelease] = useState(null)
  const [events, setEvents] = useState([])
  const [eventsError, setEventsError] = useState('')
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState('')
  const [previewInput, setPreviewInput] = useState({
    platform: 'android',
    channel: 'debug',
    packageName: '',
    currentVersionCode: '',
    currentVersionName: '',
    deviceIdHash: '',
  })
  const [pendingPublish, setPendingPublish] = useState(false)
  const [pendingRevoke, setPendingRevoke] = useState(false)
  const [revokeReason, setRevokeReason] = useState('')
  const [editOpen, setEditOpen] = useState(searchParams.get('edit') === '1')
  const [editForm, setEditForm] = useState(null)
  const [editErrors, setEditErrors] = useState({})
  const [savingEdit, setSavingEdit] = useState(false)
  const [selectedApkFile, setSelectedApkFile] = useState(null)
  const [uploadingApk, setUploadingApk] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [pendingReplaceApk, setPendingReplaceApk] = useState(false)
  const [fileInputKey, setFileInputKey] = useState(0)
  const [pendingRiskyEditConfirm, setPendingRiskyEditConfirm] = useState(false)
  const [riskyConfirmText, setRiskyConfirmText] = useState('')
  const releaseVersionLabel = formatVersionWithCode(release?.versionName, release?.versionCode)

  const load = async () => {
    try {
      setLoading(true)
      setError('')
      const item = await getMobileAppRelease(id)
      setRelease(item)
      setEditForm(toDraftForm(item))
      setPreviewInput((prev) => ({
        ...prev,
        channel: item.channel || 'debug',
        packageName: item.packageName || '',
        currentVersionCode: item.versionCode ? String(item.versionCode - 1) : '',
        currentVersionName: item.versionName || '0.0.0',
      }))
    } catch (err) {
      setError(err?.message || 'Unable to load release detail')
    } finally {
      setLoading(false)
    }
  }

  const loadEvents = async () => {
    try {
      setEventsError('')
      const rows = await getMobileAppReleaseEvents(id)
      setEvents(rows)
    } catch (err) {
      setEvents([])
      setEventsError(err?.message || 'Unable to load telemetry')
    }
  }

  useEffect(() => {
    load()
    loadEvents()
  }, [id])

  const handlePreview = async () => {
    try {
      setPreviewError('')
      const data = await previewMobileAppReleaseSelection({
        ...previewInput,
        currentVersionCode: Number(previewInput.currentVersionCode),
      })
      setPreview(data)
    } catch (err) {
      setPreview(null)
      setPreviewError(err?.message || 'Unable to preview selection')
    }
  }

  const rawAudit = useMemo(() => {
    if (!release) return '{}'
    const redacted = {
      ...release,
      apkUrl: redactSignedUrl(release.apkUrl),
    }
    return JSON.stringify(redacted, null, 2)
  }, [release])

  const handlePublish = async () => {
    try {
      await publishMobileAppRelease(id)
      showToast({ severity: 'success', title: 'Release published', message: 'Release is now published.' })
      setPendingPublish(false)
      await load()
    } catch (err) {
      showToast({ severity: 'error', title: 'Publish failed', message: err?.message || 'Unable to publish' })
    }
  }

  const handleRevoke = async () => {
    if (!revokeReason.trim()) return
    try {
      await revokeMobileAppRelease(id, revokeReason)
      showToast({ severity: 'success', title: 'Release revoked', message: 'Release has been revoked.' })
      setPendingRevoke(false)
      setRevokeReason('')
      await load()
    } catch (err) {
      showToast({ severity: 'error', title: 'Revoke failed', message: err?.message || 'Unable to revoke' })
    }
  }

  const handleSaveDraft = async () => {
    const errors = validateDraftForm(editForm || {})
    setEditErrors(errors)
    if (Object.keys(errors).length > 0) return

    const mandatoryEnabled = !Boolean(release?.mandatory) && Boolean(editForm?.mandatory)
    const rolloutToFull = Number(release?.rolloutPercent) !== 100 && Number(editForm?.rolloutPercent) === 100
    if (mandatoryEnabled || rolloutToFull) {
      setPendingRiskyEditConfirm(true)
      return
    }

    await saveDraftUpdate()
  }

  const handleConfirmRiskyEdit = async () => {
    const requiredPhrase = 'CONFIRM RISKY UPDATE'
    if (riskyConfirmText.trim() !== requiredPhrase) return
    setPendingRiskyEditConfirm(false)
    setRiskyConfirmText('')
    await saveDraftUpdate()
  }

  const saveDraftUpdate = async () => {
    if (!editForm) return

    setSavingEdit(true)
    try {
      await updateMobileAppRelease(id, {
        ...editForm,
        versionCode: Number(editForm.versionCode),
        minSupportedVersionCode: editForm.minSupportedVersionCode === '' ? null : Number(editForm.minSupportedVersionCode),
        sizeBytes: editForm.sizeBytes === '' ? null : Number(editForm.sizeBytes),
        shortDescription: editForm.shortDescription?.trim() || null,
        longDescription: editForm.releaseNotes?.trim() || null,
        releaseNotes: editForm.releaseNotes?.trim() || null,
        rolloutPercent: Number(editForm.rolloutPercent),
        expiresAt: editForm.expiresAt ? new Date(editForm.expiresAt).toISOString() : null,
      })
      showToast({ severity: 'success', title: 'Draft updated', message: 'Release draft metadata has been updated.' })
      setEditOpen(false)
      setEditErrors({})
      setPendingRiskyEditConfirm(false)
      setRiskyConfirmText('')
      await load()
    } catch (err) {
      showToast({ severity: 'error', title: 'Update failed', message: err?.message || 'Unable to update draft release' })
    } finally {
      setSavingEdit(false)
    }
  }

  const handleUploadApk = async () => {
    if (!selectedApkFile) return

    setUploadingApk(true)
    setUploadProgress(0)
    try {
      const artifact = await uploadMobileAppReleaseApk(id, selectedApkFile, (percent) => setUploadProgress(percent))
      setEditForm((prev) => ({
        ...prev,
        apkUrl: artifact.apkUrl || prev?.apkUrl || '',
        sha256: artifact.sha256 || prev?.sha256 || '',
        sizeBytes: artifact.sizeBytes == null ? (prev?.sizeBytes || '') : String(artifact.sizeBytes),
      }))
      setSelectedApkFile(null)
      setPendingReplaceApk(false)
      setFileInputKey((prev) => prev + 1)
      showToast({ severity: 'success', title: 'APK uploaded', message: 'Artifact fields were updated from upload response.' })
    } catch (err) {
      showToast({ severity: 'warning', title: 'Upload unavailable', message: err?.message || 'Unable to upload APK artifact' })
    } finally {
      setUploadingApk(false)
      setUploadProgress(0)
    }
  }

  if (loading) return <PageShell><LoadingState text="Loading release detail..." /></PageShell>
  if (error) return <PageShell><ErrorState text={error} action={<Button onClick={load}>Retry</Button>} /></PageShell>
  if (!release) return <PageShell><EmptyState title="Release not found" /></PageShell>

  return (
    <PageShell className="space-y-4">
      <PageHeader
        title={`Release ${releaseVersionLabel}`}
        subtitle="Debug/internal APK release detail and diagnostics."
        actions={(
          <div className="flex gap-2">
            {release.status === 'draft' ? (
              <Button variant="secondary" onClick={() => setEditOpen((prev) => !prev)}>{editOpen ? 'Close edit' : 'Edit draft'}</Button>
            ) : null}
            {release.status === 'draft' ? <Button onClick={() => setPendingPublish(true)}>Publish</Button> : null}
            {release.status === 'published' ? <Button variant="danger" onClick={() => setPendingRevoke(true)}>Revoke</Button> : null}
          </div>
        )}
      />

      {release.status === 'draft' && editOpen ? (
        <SectionCard title="Edit Draft Metadata" subtitle="Published releases are immutable. Only draft edits are allowed.">
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Platform" error={editErrors.platform}><Input value={editForm?.platform || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, platform: event.target.value }))} /></FormField>
            <FormField label="Channel" error={editErrors.channel}><Input data-testid="edit-release-channel" value={editForm?.channel || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, channel: event.target.value }))} /></FormField>
            <FormField label="Package name" error={editErrors.packageName}><Input value={editForm?.packageName || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, packageName: event.target.value }))} /></FormField>
            <FormField label="Version name" error={editErrors.versionName}><Input data-testid="edit-release-version-name" value={editForm?.versionName || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, versionName: event.target.value }))} /></FormField>
            <FormField label="Version code" error={editErrors.versionCode}><Input value={editForm?.versionCode || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, versionCode: event.target.value }))} /></FormField>
            <FormField label="Min supported version code" error={editErrors.minSupportedVersionCode}><Input value={editForm?.minSupportedVersionCode || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, minSupportedVersionCode: event.target.value }))} /></FormField>
            <FormField label="APK URL" error={editErrors.apkUrl}><Input value={editForm?.apkUrl || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, apkUrl: event.target.value }))} /></FormField>
            <FormField label="SHA-256" error={editErrors.sha256}><Input value={editForm?.sha256 || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, sha256: event.target.value }))} /></FormField>
            <FormField label="Size bytes"><Input value={editForm?.sizeBytes || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, sizeBytes: event.target.value }))} /></FormField>
            <FormField label="Rollout percent" error={editErrors.rolloutPercent}><Input data-testid="edit-release-rollout" value={editForm?.rolloutPercent || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, rolloutPercent: event.target.value }))} /></FormField>
            <FormField label="Expires at"><Input type="datetime-local" value={editForm?.expiresAt || ''} onChange={(event) => setEditForm((prev) => ({ ...prev, expiresAt: event.target.value }))} /></FormField>
            <FormField label="Mandatory update"><input type="checkbox" checked={Boolean(editForm?.mandatory)} onChange={(event) => setEditForm((prev) => ({ ...prev, mandatory: event.target.checked }))} /></FormField>
            <FormField
              label="Short description"
              helpText="Shown first in the update banner."
            >
              <Input
                as="textarea"
                rows={2}
                data-testid="edit-release-short-description"
                value={editForm?.shortDescription || ''}
                onChange={(event) => setEditForm((prev) => ({ ...prev, shortDescription: event.target.value }))}
              />
            </FormField>
            <div className="md:col-span-2">
              <FormField
                label="Long description / release notes"
                helpText="Use this for the full change log, including bug fixes and new features."
              >
                <Input
                  as="textarea"
                  rows={5}
                  data-testid="edit-release-long-description"
                  value={editForm?.releaseNotes || ''}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, releaseNotes: event.target.value }))}
                />
              </FormField>
            </div>
            <div className="md:col-span-2">
              <FormField label="Upload APK file" helpText="Replacing APK artifact requires explicit confirmation. If backend upload is unavailable, keep URL + SHA-256 mode.">
                <div className="flex flex-col gap-2 md:flex-row md:items-center">
                  <Input
                    key={fileInputKey}
                    data-testid="edit-release-apk-file"
                    type="file"
                    accept=".apk"
                    onChange={(event) => setSelectedApkFile(event.target.files?.[0] || null)}
                  />
                  <Button
                    variant="secondary"
                    disabled={!selectedApkFile || uploadingApk}
                    onClick={() => setPendingReplaceApk(true)}
                  >
                    {uploadingApk ? `Uploading ${uploadProgress}%` : 'Upload APK file'}
                  </Button>
                </div>
              </FormField>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button disabled={savingEdit} onClick={handleSaveDraft}>{savingEdit ? 'Saving...' : 'Save draft changes'}</Button>
          </div>
        </SectionCard>
      ) : null}

      <SectionCard title="Overview">
        <div className="grid gap-2 md:grid-cols-2">
          <div>Status: {release.status}</div>
          <div>Qualification: {release.qualificationEvidence?.invalidatedAt ? 'invalidated' : (release.qualificationEvidence?.status || 'missing')}</div>
          <div>Channel: {release.channel}</div>
          <div>Platform: {release.platform}</div>
          <div>Package: {release.packageName}</div>
          <div>Version: {releaseVersionLabel}</div>
          <div>Mandatory: {release.mandatory ? 'Yes' : 'No'}</div>
          <div>Rollout: {release.rolloutPercent}%</div>
          <div>Published: {formatDateTime(release.publishedAt)}</div>
          <div>Created: {formatDateTime(release.createdAt)}</div>
          <div>Updated: {formatDateTime(release.updatedAt)}</div>
        </div>
      </SectionCard>

      <SectionCard title="Release notes">
        <div className="space-y-3 text-sm">
          <div>
            <div className="font-medium">Short description</div>
            <div className="whitespace-pre-wrap text-muted-foreground">
              {release.shortDescription || '-'}
            </div>
          </div>
          <div>
            <div className="font-medium">Long description</div>
            <div className="whitespace-pre-wrap text-muted-foreground">
              {release.longDescription || release.releaseNotes || '-'}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="APK Artifact">
        <div className="space-y-2 text-sm">
          <div>APK URL: <span className="break-all">{redactSignedUrl(release.apkUrl)}</span></div>
          <div>SHA-256: {maskedHash(release.sha256)}</div>
          <div>Size bytes: {release.sizeBytes || '-'}</div>
          <div className="text-muted-foreground">APK upload endpoint is optional and may be unavailable in this backend stage.</div>
        </div>
      </SectionCard>

      <SectionCard title="Rollout & Policy">
        <div className="space-y-1 text-sm">
          <div>Min supported version code: {release.minSupportedVersionCode ?? '-'}</div>
          <div>Mandatory: {release.mandatory ? 'true' : 'false'}</div>
          <div>Rollout: {release.rolloutPercent}%</div>
          <div className="text-muted-foreground">Update is mandatory if mandatory=true or currentVersionCode is lower than minSupportedVersionCode.</div>
        </div>
      </SectionCard>

      <SectionCard title="Telemetry">
        {eventsError ? <EmptyState title="Telemetry unavailable" description={eventsError} /> : null}
        {!eventsError && events.length === 0 ? <EmptyState title="No telemetry yet" description="No release event records were found." /> : null}
        {!eventsError && events.length > 0 ? (
          <DataTable
            columns={[
              { key: 'status', label: 'Status', sortable: true },
              { key: 'device', label: 'Device/User', sortable: true, sortAccessor: (row) => row.userId || row.deviceIdHash || '' },
              { key: 'fromVersionCode', label: 'From', sortable: true, sortAccessor: (row) => Number(row.fromVersionCode || 0) },
              { key: 'toVersionCode', label: 'To', sortable: true, sortAccessor: (row) => Number(row.toVersionCode || 0) },
              { key: 'packageName', label: 'Package', sortable: true },
              { key: 'error', label: 'Error', sortable: true },
              { key: 'createdAt', label: 'Created', sortable: true, sortAccessor: (row) => row.createdAt || '' },
            ]}
            rows={events}
            renderRow={(row, index) => (
              <tr key={`${row.status}-${index}`}>
                <td className="px-4 py-2">{row.status}</td>
                <td className="px-4 py-2">{row.userId || row.deviceIdHash || '-'}</td>
                <td className="px-4 py-2">{row.fromVersionCode ?? '-'}</td>
                <td className="px-4 py-2">{row.toVersionCode ?? '-'}</td>
                <td className="px-4 py-2">{row.packageName || '-'}</td>
                <td className="px-4 py-2">{row.error || '-'}</td>
                <td className="px-4 py-2">{formatDateTime(row.createdAt)}</td>
              </tr>
            )}
          />
        ) : null}
      </SectionCard>

      <SectionCard title="App Config Preview" subtitle="Preview selection against current backend policy.">
        <div className="grid gap-3 md:grid-cols-3">
          <FormField label="Platform"><Input value={previewInput.platform} onChange={(event) => setPreviewInput((prev) => ({ ...prev, platform: event.target.value }))} /></FormField>
          <FormField label="Channel"><Input value={previewInput.channel} onChange={(event) => setPreviewInput((prev) => ({ ...prev, channel: event.target.value }))} /></FormField>
          <FormField label="Package name"><Input value={previewInput.packageName} onChange={(event) => setPreviewInput((prev) => ({ ...prev, packageName: event.target.value }))} /></FormField>
          <FormField label="Current version code"><Input data-testid="preview-current-version-code" value={previewInput.currentVersionCode} onChange={(event) => setPreviewInput((prev) => ({ ...prev, currentVersionCode: event.target.value }))} /></FormField>
          <FormField label="Current version name"><Input value={previewInput.currentVersionName} onChange={(event) => setPreviewInput((prev) => ({ ...prev, currentVersionName: event.target.value }))} /></FormField>
          <FormField label="Device id / hash"><Input data-testid="preview-device-id" value={previewInput.deviceIdHash} onChange={(event) => setPreviewInput((prev) => ({ ...prev, deviceIdHash: event.target.value }))} /></FormField>
        </div>
        <div className="mt-3"><Button onClick={handlePreview}>Preview selection</Button></div>
        {previewError ? <p className="mt-2 text-sm text-danger">{previewError}</p> : null}
        {preview ? <pre className="mt-2 overflow-auto rounded border border-outline bg-surface-elevated p-3 text-xs">{JSON.stringify(preview, null, 2)}</pre> : null}
      </SectionCard>

      <SectionCard title="Audit / Raw JSON">
        <pre className="overflow-auto rounded border border-outline bg-surface-elevated p-3 text-xs">{rawAudit}</pre>
      </SectionCard>

      <ConfirmDialog
        open={pendingPublish}
        title="Publish release"
        message="Publish this draft release?"
        confirmText="Publish"
        onConfirm={handlePublish}
        onCancel={() => setPendingPublish(false)}
      />

      <ConfirmDialog
        open={pendingRevoke}
        title="Revoke release"
        message="Revoke this published release?"
        confirmText="Revoke"
        confirmVariant="danger"
        onConfirm={handleRevoke}
        onCancel={() => {
          setPendingRevoke(false)
          setRevokeReason('')
        }}
      >
        <FormField label="Reason is required">
          <Input value={revokeReason} onChange={(event) => setRevokeReason(event.target.value)} />
        </FormField>
      </ConfirmDialog>

      <ConfirmDialog
        open={pendingReplaceApk}
        title="Replace APK artifact"
        message={selectedApkFile
          ? `Upload ${selectedApkFile.name} and replace APK URL/SHA-256/size metadata from server response?`
          : 'Upload selected APK and replace artifact metadata?'}
        confirmText="Replace APK"
        onConfirm={handleUploadApk}
        onCancel={() => setPendingReplaceApk(false)}
      />

      <ConfirmDialog
        open={pendingRiskyEditConfirm}
        title="Confirm risky draft change"
        message="This edit enables mandatory updates or sets rollout to 100%. Type CONFIRM RISKY UPDATE to continue."
        confirmText="Apply risky change"
        onConfirm={handleConfirmRiskyEdit}
        onCancel={() => {
          setPendingRiskyEditConfirm(false)
          setRiskyConfirmText('')
        }}
      >
        <FormField label="Type CONFIRM RISKY UPDATE to continue">
          <Input data-testid="risky-edit-confirm-input" value={riskyConfirmText} onChange={(event) => setRiskyConfirmText(event.target.value)} />
        </FormField>
      </ConfirmDialog>
    </PageShell>
  )
}
